// vue
import { ref } from '@vue/composition-api';

// services
import { useService } from '@/vue3/api/services';

// configs
import pdfStyle from '@/vue3/configs/mapbox/courseScreenshot';

// libs
import jsPDF from 'jspdf';

// models
import { GeoRecord, MeasureRecord } from '../api/models';

// hooks
import { useMeasureRecord } from '@/vue3/hooks/measureRecordHook';
import { useGeoRecord } from '@/vue3/hooks/geoRecordHook';
import { useStore } from '@/store';
import { useNotify } from '@/vue3/hooks/notifyHook';

// images
import pdfLogoImg from '@/assets/clere-hub-pdf.png';
import pdfLogoBadge from '@/assets/clere-hub-only.png';
import { useMapbox } from './mapboxHook';

export const useCoursePdf = () => {
    // hooks
    const store = useStore();
    const { addSurroundingVertex } = useMeasureRecord();
    const { generateMapboxFeature } = useGeoRecord();
    const { successToast, errorToast } = useNotify();

    // services
    const { courseScreenshot, getAvailableTiles, getAllIconFiles } =
        useService();

    const { getAllMosaicsByCourseSchemaID } = useMapbox();
    // refs
    const isGeneratingPdf = ref(false);

    const jspdf = ref(new jsPDF('l', 'mm', 'a4'));
    const jspdfp = ref(new jsPDF('p', 'mm', 'a4'));

    const pdfImages = ref<any[]>([]);

    const svgLayerList = ref<any[]>([]);
    const geoRecordsWithSvg = ref<any[]>([]);
    const svgArray = ref<any[]>([]);

    const isMeters = store.state.isMeters;

    // methods
    const blobToBase64 = (blob) => {
        const reader = new FileReader();

        reader.readAsDataURL(blob);

        return new Promise((resolve) => {
            reader.onloadend = () => {
                resolve(reader.result);
            };
        });
    };

    const generateGeoRecords = (geoRecords: GeoRecord[]) => {
        const geoRecordFeatures = geoRecords.map((geoRecord) => {
            return generateMapboxFeature(geoRecord);
        });

        return geoRecordFeatures;
    };

    const checkLayerSource = (layer: any): boolean => {
        return layer.source === 'clere'
            ? true
            : layer.source === 'clere-mosaic'
            ? true
            : layer.source === 'clere-raster'
            ? true
            : false;
    };

    const addSVGLayers = (maplayers: any): void => {
        let image: any = [];

        svgLayerList.value.forEach((svg, index) => {
            maplayers.push({
                id: svg.iconFileName,
                type: 'symbol',
                source: svg.id.toString(),
                layout: {
                    'icon-image': svg.iconFileName,
                    'icon-size': 1,
                },
            });

            image[index] = {
                url: `${store.state.apiUrl}/assets/geo-record-icons/${svg.iconFileName}`,
                sdf: false,
                pixelRatio: 1,
                name: svg.iconFileName,
            };
        });

        svgArray.value = image;

        return maplayers;
    };

    const courseLayerStyling = (maplayers: any): any => {
        maplayers.forEach((layer: any) => {
            if (!checkLayerSource(layer)) return;

            layer.minzoom = 0;
            layer.maxzoom = 40;

            switch (layer.id) {
                case 'fwy-outline':
                    layer.paint = {
                        'line-opacity': 0.5,
                        'line-color': '#006400',
                        'line-width': 1,
                    };
                    break;
                case 'bunker':
                    layer.paint = {
                        'fill-color': '#fdffcf',
                        'fill-opacity': 0.9,
                    };
                    break;
                case 'bunker-outline':
                    layer.paint = {
                        'line-color': '#a2a37a',
                        'line-width': 1,
                        'line-opacity': 1,
                    };
                    break;
                case 'green-outline':
                    layer.paint = {
                        'line-color': '#006400',
                        'line-width': 1,
                        'line-opacity': 1,
                    };
                    break;
            }
        });

        addSVGLayers(maplayers);

        return maplayers;
    };

    const removeDuplicateSvgLayer = (): void => {
        const unique = [...new Set(svgLayerList.value.map((item) => item.id))];

        svgLayerList.value = unique.map((id) => {
            return svgLayerList.value.find((item) => item.id === id);
        });
    };

    const removeNullSvgLayer = (): void => {
        svgLayerList.value = svgLayerList.value.filter(
            (item) => item.iconFileName,
        );
    };

    const generateGeoRecordIcons = async (geoRecords: any) => {
        const iconFiles = await getAllIconFiles();

        const geoRecordFeatures = geoRecords.filter((geoRecord: any) => {
            return (
                geoRecord.geometry.type === 'Point' &&
                geoRecord.geoRecordCategoryId
            );
        });

        geoRecordFeatures.forEach((geoRecord: any) => {
            const iconFile = iconFiles.find((iconFile) => {
                return iconFile.id === geoRecord.geoRecordCategoryId;
            });

            if (!iconFile) return;

            svgLayerList.value.push(iconFile);
        });

        geoRecordsWithSvg.value = geoRecordFeatures;

        removeNullSvgLayer();

        removeDuplicateSvgLayer();
    };

    const generateScreenshot = async (
        courseExtent: any,
        pdfVersion: number,
        geoRecords: GeoRecord[],
        measureRecords: MeasureRecord[],
        maplayers: any,
        zoomedMapBbox: any,
    ) => {
        geoRecords.forEach((geoRecord: any) => {
            delete geoRecord.accountId;
            delete geoRecord.assigneeAccountId;
            delete geoRecord.assigneeId;
            delete geoRecord.dueDate;
            delete geoRecord.dueDateUnix;
            delete geoRecord.isNonGeometry;
            delete geoRecord.isPrivate;
            delete geoRecord.isRectangule;
            delete geoRecord.isTask;
            delete geoRecord.notes;
            delete geoRecord.projectId;
            delete geoRecord.statusId;
            delete geoRecord.startDate;
            delete geoRecord.startDateUnix;
            delete geoRecord.geoRecordModelId;
            delete geoRecord.isRectangle;
            delete geoRecord.iteration;
            delete geoRecord.rotation;
            delete geoRecord.smoothCount;
            delete geoRecord.isDimensionLine;
            delete geoRecord.courseSchemaId;
            delete geoRecord.endDate;
            delete geoRecord.endDateUnix;
            delete geoRecord.organizationId;
            delete geoRecord.geoRecordCategoryOrder;
            delete geoRecord.images;
            delete geoRecord.image;
        });

        await generateGeoRecordIcons(geoRecords);

        let isMosaicAvialable = false;

        const courseName = store.getters.currentCourseSchema.name;

        const tiles = await getAvailableTiles();

        let geoRecordsList = generateGeoRecords(geoRecords);
        let size = pdfOption.pageSize;
        let type = pdfOption.selection;
        let resolution = pdfOption.resolution;
        pdfStyle.layers = courseLayerStyling(maplayers);

        pdfStyle.sources['geo-records'] = {
            type: 'geojson',
            minzoom: 0,
            maxzoom: 25,
            buffer: 256,
            data: {
                type: 'FeatureCollection',
                features: [...geoRecordsList],
            },
        };

        geoRecordsWithSvg.value.forEach((geoRecord: any) => {
            pdfStyle.sources[geoRecord.id.toString()] = {
                type: 'geojson',
                minzoom: 0,
                maxzoom: 25,
                buffer: 256,
                data: {
                    type: 'FeatureCollection',
                    features: [generateMapboxFeature(geoRecord)],
                },
            };
        });

        const measureFeatures: any[] = [];

        if (measureRecords) {
            measureRecords.forEach((record, i) => {
                const recordFeaturesLength = Object.keys(
                    record.features,
                ).length;

                if (recordFeaturesLength === 0) {
                    return;
                }

                if (record.features[0].geometry.type === 'Polygon') {
                    record.features[0].properties.measureRecordId = record.id;
                    measureFeatures.push(record.features[0]);
                    return;
                }

                measureFeatures.push(
                    ...addSurroundingVertex([], record, store.state.isMeters),
                );
            });
        }

        const measureRecordSource = {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: <any>[],
            },
        };

        measureFeatures.forEach((record) => {
            const type = record.geometry.type;

            if (type === 'Polygon') {
                measureRecordSource.data.features.push({
                    id: record.properties.measureRecordId,
                    type: 'Feature',
                    geometry: record.geometry,
                    properties: {
                        measureRecordId: record.properties.measureRecordId,
                        isVisible: true,
                        measurement: record.properties.measurement,
                        measurementLabel: `${
                            Math.round(
                                record.properties.measurement *
                                    100 *
                                    (isMeters ? 1 : 1.195990046301),
                            ) / 100
                        } ${isMeters ? `m²` : `yd²`}`,
                        totalMeasurementLabel: null,
                    },
                });
            }

            if (type === 'LineString') {
                measureRecordSource.data.features.push({
                    id: record.properties.measureRecordId,
                    type: 'Feature',
                    geometry: record.geometry,
                    properties: {
                        measureRecordId: record.properties.measureRecordId,
                        isVisible: true,
                        measurement: record.properties.measurement,
                        measurementLabel: `${
                            Math.round(
                                record.properties.measurement *
                                    100 *
                                    (isMeters ? 1 : 1.09361),
                            ) / 100
                        } ${isMeters ? `m` : `yd`}`,
                        totalMeasurementLabel: null,
                    },
                });
            }

            if (type === 'Point') {
                measureRecordSource.data.features.push({
                    id: record.properties.measureRecordId,
                    type: 'Feature',
                    geometry: record.geometry,
                    properties: {
                        measureRecordId: record.properties.measureRecordId,
                        isVisible: true,
                        measurement: null,
                        measurementLabel: null,
                        totalMeasurementLabel: `${
                            Math.round(
                                record.properties.measurementTotal *
                                    100 *
                                    (isMeters ? 1 : 1.09361),
                            ) / 100
                        } ${isMeters ? 'm' : 'yd'}`,
                    },
                });
            }
        });

        const courseMosaic = await getAllMosaicsByCourseSchemaID(
            store.getters.currentCourseSchema.id,
        );

        let mosaicName =
            courseMosaic.length > 0 ? courseMosaic[0].key : undefined;

        pdfStyle.sources['measure-records'] = measureRecordSource;

        tiles.forEach(async (tile: any) => {
            if (
                tile.id.indexOf(courseName) !== -1 &&
                tile.id.indexOf('dtm') === -1
            ) {
                isMosaicAvialable = true;
            }

            if (isMosaicAvialable) {
                pdfStyle.sources['clere-mosaic'] = {
                    type: 'raster',
                    minzoom: 0,
                    maxzoom: 25,
                    tileSize: 256,
                    url: `${store.state.MaptilerUrl}/data/${mosaicName}.json`,
                };
            }

            if (!isGeneratingPdf.value) {
                try {
                    isGeneratingPdf.value = true;

                    jspdf.value = new jsPDF(
                        'l',
                        'mm',
                        size == 'A4 Portrait' || size == 'A4 Landscape'
                            ? 'a4'
                            : 'a3',
                    );
                    jspdfp.value = new jsPDF(
                        'p',
                        'mm',
                        size == 'A4 Portrait' || size == 'A4 Landscape'
                            ? 'a4'
                            : 'a3',
                    );

                    let reswidth = resolution == 'Normal' ? 310 * 3 : 510 * 3;
                    let resheight =
                        resolution == 'Normal'
                            ? pdfOption.pageSize === 'A4 Landscape' ||
                              pdfOption.pageSize === 'A3 Landscape'
                                ? 310 * 3
                                : 397 * 3
                            : pdfOption.pageSize === 'A4 Landscape' ||
                              pdfOption.pageSize === 'A3 Landscape'
                            ? 510 * 3
                            : 597 * 3;

                    pdfImages.value = [];
                    let promises: any = {};

                    if (type == 'All holes') {
                        for (let i = 1; i <= 18; i++) {
                            if (
                                store.getters.currentCourseSchema.name ==
                                    'jumeirah' &&
                                (i == 4 || i == 6)
                            ) {
                                reswidth = 1010 * 3;
                                resheight =
                                    pdfOption.pageSize === 'A4 Landscape' ||
                                    pdfOption.pageSize === 'A3 Landscape'
                                        ? 1010 * 3
                                        : 1097 * 3;
                            }

                            promises[i] = courseScreenshot(
                                reswidth,
                                resheight,
                                courseExtent.holes[i].geom.bbox,
                                courseExtent.holes[i].azimuth,
                                pdfStyle,
                                svgArray.value,
                            );
                        }

                        for (let i = 1; i <= 18; i++) {
                            const imageData = await blobToBase64(
                                await promises[i],
                            );

                            let pdfImage = {
                                id: i,
                                img: imageData,
                                values: { x: 0, y: 0 },
                                isLeft: true,
                                greenDepth: 30,
                            };

                            pdfImages.value.push(pdfImage);
                            pdfOption.pageCount = i;
                        }
                    } else if (type == 'Entire course') {
                        if (
                            store.getters.currentCourseSchema.name == 'jumeirah'
                        ) {
                            reswidth = 1010 * 3;
                            resheight =
                                pdfOption.pageSize === 'A4 Landscape' ||
                                pdfOption.pageSize === 'A3 Landscape'
                                    ? 1010 * 3
                                    : 1097 * 3;
                        }

                        promises[1] = courseScreenshot(
                            reswidth,
                            resheight,
                            courseExtent.course.bbox,
                            0,
                            pdfStyle,
                            svgArray.value,
                        );

                        const imageData = await blobToBase64(await promises[1]);

                        let pdfImage = {
                            id: 1,
                            img: imageData,
                            values: { x: 0, y: 0 },
                            isLeft: true,
                            greenDepth: 30,
                        };

                        pdfImages.value.push(pdfImage);
                        pdfOption.pageCount = 1;
                    } else if (type == 'Map extent') {
                        reswidth =
                            resolution == 'Normal'
                                ? pdfOption.pageSize === 'A4 Landscape' ||
                                  pdfOption.pageSize === 'A3 Landscape'
                                    ? 400 * 3
                                    : 310 * 3
                                : pdfOption.pageSize === 'A4 Landscape' ||
                                  pdfOption.pageSize === 'A3 Landscape'
                                ? 600 * 3
                                : 520 * 3;
                        resheight =
                            resolution == 'Normal'
                                ? pdfOption.pageSize === 'A4 Landscape' ||
                                  pdfOption.pageSize === 'A3 Landscape'
                                    ? 310 * 3
                                    : 400 * 3
                                : pdfOption.pageSize === 'A4 Landscape' ||
                                  pdfOption.pageSize === 'A3 Landscape'
                                ? 520 * 3
                                : 600 * 3;

                        promises[1] = courseScreenshot(
                            reswidth,
                            resheight,
                            zoomedMapBbox,
                            0,
                            pdfStyle,
                            svgArray.value,
                        );

                        const imageData = await blobToBase64(await promises[1]);

                        let pdfImage = {
                            id: 1,
                            img: imageData,
                            values: { x: 0, y: 0 },
                            isLeft: true,
                            greenDepth: 30,
                        };

                        pdfImages.value.push(pdfImage);
                        pdfOption.pageCount = 1;
                    } else if (type == 'Select hole') {
                        if (
                            store.getters.currentCourseSchema.name ==
                                'jumeirah' &&
                            (pdfOption.holeNo == 4 || pdfOption.holeNo == 6)
                        ) {
                            reswidth = 1010 * 3;
                            resheight =
                                pdfOption.pageSize === 'A4 Landscape' ||
                                pdfOption.pageSize === 'A3 Landscape'
                                    ? 1010 * 3
                                    : 1097 * 3;
                        }

                        promises[1] = courseScreenshot(
                            reswidth,
                            resheight,
                            courseExtent.holes[pdfOption.holeNo].geom.bbox,
                            courseExtent.holes[pdfOption.holeNo].azimuth,
                            pdfStyle,
                            svgArray.value,
                        );

                        const imageData = await blobToBase64(await promises[1]);

                        let pdfImage = {
                            id: 1,
                            img: imageData,
                            values: { x: 0, y: 0 },
                            isLeft: true,
                            greenDepth: 30,
                        };

                        pdfImages.value.push(pdfImage);
                        pdfOption.pageCount = 1;
                    } else if (type === 'Zoomed in green') {
                        promises[1] = courseScreenshot(
                            pdfOption.pageSize === 'A4 Portrait'
                                ? 500
                                : pdfOption.pageSize === 'A3 Portrait'
                                ? 600
                                : 700,
                            pdfOption.pageSize === 'A4 Portrait'
                                ? 700
                                : pdfOption.pageSize === 'A3 Portrait'
                                ? 800
                                : 500,
                            courseExtent.greens[pdfOption.holeNo].geom.bbox,
                            courseExtent.greens[pdfOption.holeNo].azimuth,
                            pdfStyle,
                            svgArray.value,
                        );

                        const imageData = await blobToBase64(await promises[1]);

                        let pdfImage = {
                            id: 1,
                            img: imageData,
                            values: { x: 0, y: 0 },
                            isLeft: true,
                            greenDepth: 30,
                        };

                        pdfImages.value.push(pdfImage);
                        pdfOption.pageCount = 1;
                    }

                    pdfImages.value.sort((a: any, b: any) => {
                        if (a.id < b.id) return -1;
                        if (b.id < a.id) return 1;
                        return 0;
                    });

                    // Set all the numbers as variables as we need to recalculate when
                    // scaling to portrait A4 for version 3

                    makesScreenshotSheet();

                    if (!isGeneratingPdf.value) {
                        errorToast('Failed to generate PDF.');
                        return;
                    }

                    let fileName =
                        'ClereGolfHub_' +
                        store.getters.currentCourseSchema.name;

                    jspdf.value.save(fileName);
                    successToast('PDF generated successfully.');
                    isGeneratingPdf.value = false;
                } catch (e) {
                    window.console.log(e);
                    errorToast('Failed to generate PDF.');
                    isGeneratingPdf.value = false;
                }
            }
        });
    };

    /*const makesScreenshotSheet = (
        holeStart,
        dropDownBy,
        moveAccrossBy,
        scaleAdjust,
        holeEnd,
        pdfVersion,
    ) => {*/

    const makesScreenshotSheet = () => {
        pdfImages.value.sort((a: any, b: any) => {
            return a.id - b.id;
        });

        jspdf.value.setFont('helvetica', 'normal');
        jspdf.value.setFontSize(14);
        jspdf.value.setTextColor('gray');

        jspdfp.value.setFont('helvetica', 'normal');
        jspdfp.value.setFontSize(14);
        jspdfp.value.setTextColor('gray');

        // Check if Landscape mode is required
        let isLandscape =
            pdfOption.pageSize === 'A4 Landscape' ||
            pdfOption.pageSize === 'A3 Landscape';
        let width, height;

        if (
            pdfOption.pageSize === 'A4 Portrait' ||
            pdfOption.pageSize === 'A4 Landscape'
        ) {
            width = isLandscape ? 297 : 210;
            height = isLandscape ? 208 : 297;
        } else {
            // A3 size
            width = isLandscape ? 420 : 297;
            height = isLandscape ? 293 : 420;
        }

        if (!isLandscape) {
            jspdf.value = jspdfp.value; // this sets page to portrait when required
        }

        let imgBadge;
        let img;

        const schema = store.getters.currentCourseSchema.displayName;
        const schemaTag = store.getters.currentCourseSchema.tag;

        if (!schema) {
            isGeneratingPdf.value = false;
            return window.console.log('Error: Course name not valid');
        }

        const displayName = schemaTag
            ? schema.charAt(0).toUpperCase() +
              schema.slice(1) +
              ' - ' +
              schemaTag
            : schema;

        for (let i = 1; i <= pdfOption.pageCount; i++) {
            jspdf.value.addImage(
                pdfImages.value[i - 1].img,
                'PNG',
                width * 0.05,
                height * 0.05,
                width * 0.9,
                height * 0.9,
            );

            let modifiedDispName =
                pdfOption.selection === 'All holes'
                    ? displayName + ' Hole: ' + i
                    : pdfOption.selection === 'Select hole'
                    ? displayName + ' Hole: ' + pdfOption.holeNo
                    : pdfOption.selection === 'Zoomed in green'
                    ? displayName + ' Hole: ' + pdfOption.holeNo
                    : displayName;

            imgBadge = new Image();

            imgBadge.src = pdfLogoBadge;

            img = new Image();

            img.src = pdfLogoImg;

            if (isLandscape) {
                jspdf.value.text(
                    modifiedDispName,
                    width / 2,
                    pdfOption.pageSize === 'A4 Landscape' ? 8 : 10,
                    {
                        align: 'center',
                    },
                );

                jspdf.value.addImage(
                    imgBadge,
                    'png',
                    pdfOption.pageSize === 'A4 Landscape' ? 9 : 10,
                    height - (pdfOption.pageSize === 'A4 Landscape' ? 8 : 12),
                    pdfOption.pageSize === 'A4 Landscape' ? 6 : 8,
                    pdfOption.pageSize === 'A4 Landscape' ? 6 : 10,
                );
                jspdf.value.addImage(
                    img,
                    'png',
                    width - (pdfOption.pageSize === 'A4 Landscape' ? 54 : 58),
                    height - (pdfOption.pageSize === 'A4 Landscape' ? 8 : 12),
                    50,
                    pdfOption.pageSize === 'A4 Landscape' ? 6 : 10,
                );
            } else {
                jspdf.value.text(modifiedDispName, width / 2, 10, {
                    align: 'center',
                });
                jspdf.value.addImage(imgBadge, 'png', 10, height - 10, 6, 6);
                jspdf.value.addImage(
                    img,
                    'png',
                    width - 52,
                    height - 10,
                    44,
                    6,
                );
            }

            if (i !== pdfOption.pageCount) {
                jspdf.value.addPage();
            }
        }
    };

    const pdfSelection = [
        { selection: 'All holes' },
        { selection: 'Select hole' },
        { selection: 'Entire course' },
        { selection: 'Map extent' },
        { selection: 'Zoomed in green' },
    ];

    const pdfPageResolution = [
        { resolution: 'Normal' },
        { resolution: 'High' },
    ];

    const pdfPageSize = [
        { size: 'A4 Portrait' },
        { size: 'A3 Portrait' },
        { size: 'A4 Landscape' },
        { size: 'A3 Landscape' },
    ];

    const pdfHoleOption = [
        { hole: 1 },
        { hole: 2 },
        { hole: 3 },
        { hole: 4 },
        { hole: 5 },
        { hole: 6 },
        { hole: 7 },
        { hole: 8 },
        { hole: 9 },
        { hole: 10 },
        { hole: 11 },
        { hole: 12 },
        { hole: 13 },
        { hole: 14 },
        { hole: 15 },
        { hole: 16 },
        { hole: 17 },
        { hole: 18 },
    ];

    const pdfOption = {
        selection: 'All holes',
        pageSize: 'A4 Portrait',
        resolution: 'Normal',
        holeNo: 1,
        pageCount: 0,
        currentGreen: 1,
    };

    return {
        generateScreenshot,
        isGeneratingPdf,
        pdfOption,
        pdfHoleOption,
        pdfPageSize,
        pdfPageResolution,
        pdfSelection,
    };
};
